if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        // Only hand over an element that exists. `openedBy` is null after a `hide(true)`, and
        // storing that would leave the notification pointing at nothing, so the next add-to-cart
        // would restore focus nowhere instead of back to the button that opened it.
        if (cartNotification && this.openedBy) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner')?.classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => {
            // A 404 or a 500 still resolves: `response.text()` hands back Shopify's HTML error
            // page, which parses to a document with no `product-info` in it. Nothing downstream
            // distinguishes that from a good response, so the check has to happen here or the
            // failure is reported as success and diagnosed as a crash further down.
            if (!response.ok) {
              throw new Error(`Quick add: ${response.status} ${response.statusText}`);
            }
            return response.text();
          })
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            if (!productElement) throw new Error('Quick add: response contained no product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();

            super.show(opener);
          })
          .catch((error) => {
            // Without this the only trace was an unhandled rejection in the console: `.finally`
            // below put the button back to normal and the shopper saw it do nothing at all.
            console.error(error);
            this.showLoadError(opener);
          })
          .finally(() => {
            // Always, on both paths, so a failed open leaves the button clickable again rather
            // than stuck behind a spinner with no working dialog behind it.
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner')?.classList.add('hidden');
          });
      }

      /*
       * A failed load is reported inside the modal rather than left to the shopper to work out.
       *
       * Putting the message on the opener does not work: the quick-add button is named with
       * `aria-labelledby`, so overwriting its text fights that, and a `role="alert"` region
       * dropped into a card grid is announced with no context about what failed. Inside the
       * dialog the message is the only content, focus is already trapped there by `super.show()`,
       * and `role="alert"` is read on insertion -- the same shape `snippets/buy-buttons.liquid`
       * uses for the add-to-cart error.
       */
      showLoadError(opener) {
        this.modalContent.innerHTML = '';

        try {
          // `super.show()` is what assigns `openedBy`. If it throws, `openedBy` is left naming a
          // button while no dialog is open, and the next `hide()` would try to restore focus into
          // a dialog that was never displayed.
          super.show(opener);
        } catch (error) {
          console.error(error);
          this.openedBy = null;
          this.modalContent.innerHTML = '';
          return;
        }

        // Inserted after `show()`, not before: a live region has to change while the dialog is
        // already open and focused, or some screen readers announce nothing at all.
        const wrapper = document.createElement('div');
        wrapper.className = 'page-width';

        const error = document.createElement('p');
        error.className = 'quick-add__error';
        error.setAttribute('role', 'alert');
        // textContent, not innerHTML: the message is a translated string from the page and must
        // never be parsed as markup.
        error.textContent =
          window.variantStrings?.quickAddLoadError || "Couldn't load these options. Please try again.";

        wrapper.appendChild(error);
        this.modalContent.appendChild(wrapper);
      }

      preprocessHTML(productElement) {
        // Reachable both from `show()` above and from `product-info:loaded` above, and both can
        // hand over nothing when a section request returns an error page.
        if (!productElement) return;

        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
